import { cva, type VariantProps } from 'class-variance-authority';

/**
 * DropdownMenu visual variants — mirror Figma "Flashka Design System"
 * file `YZK9r3wDhvw1pY60KlVEIj`, section `1130:17622` ("Dropdown_menu").
 *
 * Tokens read directly from Figma (compact node `1284:12478` + siblings):
 *   - Fill:   `brand/secondary/130` = `secondary-100` @ 70%
 *             (`#f5f5f5b2` light / `#171717b2` dark — auto-inverts) over a
 *             4-px GLASS backdrop blur (`web:backdrop-blur-sm`).
 *   - Border: `secondary-200` (`#e5e5e5` light / `#262626` dark)
 *   - Shadow: `0 2px 8px rgba(0,0,0,0.06)` (Figma DROP_SHADOW `#0000000F`)
 *   - Padding 8 px, row gap 4 px — constant across every size band.
 *
 * `size` scales ONLY the panel's corner radius to match the row band it
 * hosts (verified node-by-node: 56→24 `1284:12478`, 48→24 `1127:16259`,
 * 44→20, 32→18 `1291:7169` — i.e. the `MenuListItem` row radius + 6).
 * Everything else is size-invariant, so `size` is a single-axis radius map.
 *
 * Figma's "Compact" and "Extended-desktop" frames are pixel-identical panel
 * chrome (they differ only in how many rows they show), so there is NO
 * compact-vs-extended styling axis. "Extended-mobile" is the bottom-sheet
 * form — a drag handle on top — opted into via `withHandle` on the container.
 *
 * Dark mode is automatic: the whole `secondary` palette auto-inverts via CSS
 * vars, so `bg-secondary-100/70` renders `#f5f5f5b2` in light and `#171717b2`
 * in dark with no `dark:` swaps. Native has no `backdrop-filter`, so the
 * `web:`-gated blur drops there — the translucent fill stays (same trade-off
 * the shared Button glass makes).
 */
export const dropdownMenuVariants = cva(
  // Panel container — rounded glass card with a soft 1-px border and shadow.
  //
  // `flex flex-col` stacks rows. No `overflow-hidden`: the rows' inner
  // corners already fit within the panel's outer corner, and clipping would
  // eat a focused row's ring / a header that runs to the panel edge.
  //
  // The shadow is hand-rolled (not `shadow-lg`) so the spec value is honoured
  // to the pixel — every popup chrome in the design system lands here, so a
  // single point of drift hurts everywhere.
  // `flashka-dropdown-menu` is a style-free MARKER: on web it lets the shared
  // index.css rule move the frosted-glass `backdrop-filter` onto the Radix
  // popper wrapper. Radix positions popovers with a `transform` on that
  // wrapper, and a transformed ancestor makes a descendant's `backdrop-filter`
  // sample an empty backdrop (no visible blur) — so the panel's own
  // `web:backdrop-blur-sm` only works when it's NOT inside a Radix popover
  // (standalone / custom compositions). The wrapper rule restores the blur for
  // the popover case (its own transform doesn't break its own backdrop-filter).
  'flashka-dropdown-menu flex flex-col border border-secondary-200 gap-1 p-2 ' +
    'bg-secondary-100/70 web:backdrop-blur-sm ' +
    'shadow-[0_2px_8px_0_rgba(0,0,0,0.06)]',
  {
    variants: {
      // Corner radius per row band (see header). Padding / gap / fill /
      // shadow / border are all size-invariant, so this is the only axis.
      size: {
        xl: 'rounded-3xl', // 24 — hosts MenuListItem xl (56) rows
        l: 'rounded-3xl', // 24 — hosts MenuListItem l (48) rows
        m: 'rounded-[20px]', // hosts MenuListItem m (44) rows
        s: 'rounded-[18px]', // hosts MenuListItem s (32) rows
      },
    },
    defaultVariants: { size: 'xl' },
  },
);

export type DropdownMenuVariantProps = VariantProps<typeof dropdownMenuVariants>;

/**
 * Numeric panel radii (px) matching the `size` variant's `rounded-*` classes
 * above. Needed by the native frosted-glass layer (the shared `GlassBackground`), which
 * clips an `expo-blur` BlurView to the panel shape — RN can't read the radius
 * off a Tailwind class, so it takes the px value directly.
 */
export const DROPDOWN_MENU_RADIUS_PX: Record<
  NonNullable<DropdownMenuVariantProps['size']>,
  number
> = { xl: 24, l: 24, m: 20, s: 18 };

/**
 * The panel's `p-2` padding in px — size-invariant (see header). Needed by
 * `DropdownSubContent`, which anchors its flyout to a trigger ROW and must add
 * this back to offset from the panel's border rather than the row's edge.
 */
export const DROPDOWN_MENU_PADDING_PX = 8;
